import type { DocumentPlan, PlannedElement } from "./document-plan";
import {
  audioSegmentRole,
  audioTrackKeptMs,
  type AudioSegmentRole,
  type ProjectAudioRecord,
  type ProjectAudioTrack,
} from "./types";

/**
 * Pinning a lane track to a run of the film.
 *
 * The lane is POSITIONED: every track states the output time it begins at. That
 * is what lets a voiceover sit over one particular shot, and what lets two
 * overlap or leave a pause between them — and it is also what makes a lane of
 * narration exhausting to keep straight, because a position is a NUMBER and the
 * thing it has to line up with is a cut. Trim a shot by half a second and every
 * take after it is half a second wrong, silently.
 *
 * Pinning inverts that, and it is now the ONLY way a track is placed. The
 * operator says which SCENES a take belongs to — a first and a last, picked off
 * the timeline — and the position becomes a consequence of the film rather than
 * a second copy of it. Move the scenes, retrim them, insert one before them,
 * and the take is still on its scenes.
 *
 * There used to be a free mode where each track stated its own start. It is
 * gone: two ways to place a track meant every control had to say which one you
 * were in, and the answer to "where does this line go" was a number in one mode
 * and a pair of scenes in the other. A track with no span yet simply keeps
 * whatever `startMs` it has, which is where it was last heard.
 *
 * ## `startMs` stays authoritative
 *
 * A pinned track's `startMs` is DERIVED but still stored, and reconciled after
 * anything that could move it (see `applySpans`). The alternative — deriving it
 * at read time — would mean teaching the preview, both export engines and the
 * manifest builder about spans, and each of those receives a stripped track
 * shape with no document to resolve against. One write-side reconciliation
 * against nine read-side ones.
 *
 * ## One take per scene
 *
 * Spans of the same ROLE may not overlap: a scene has one voiceover, or none.
 * Enforced by `availableElements`, which only offers scenes that are free — so
 * an overlap is not something the UI can express rather than something it has
 * to detect afterwards. Across roles there is no such rule: a bed under a scene
 * and a voice over it is the ordinary case.
 */

/** One track's span, resolved against the film. */
export interface SpanPlacement {
  trackId: string;
  role: AudioSegmentRole;
  /** The scenes it covers, in film order. Empty when it has no usable span. */
  elementIds: string[];
  /** Output time the span starts, and ends. */
  startMs: number;
  endMs: number;
  /** The scenes' combined length. */
  spanMs: number;
  /** The track's kept audio length. */
  keptMs: number;
  /**
   * How far the audio outruns its scenes. Zero when it fits.
   *
   * Only a problem for SPEECH, and only in one direction: audio shorter than
   * its scenes simply stops, and the film plays on in silence, which is what a
   * pause between lines is. Longer means the end of the sentence is cut off.
   */
  overrunMs: number;
}

/** A track's stated span, or null when it has none. */
function spanOf(
  track: Pick<ProjectAudioTrack, "spanFromElementId" | "spanToElementId">,
): { from: string; to: string } | null {
  const from = track.spanFromElementId;
  const to = track.spanToElementId;
  if (!from || !to) return null;
  return { from, to };
}

/**
 * Resolve every pinned track's span against the film.
 *
 * A span whose ends are gone — the scene was deleted, or hidden from the film —
 * resolves to nothing rather than to a guess. The track then keeps whatever
 * `startMs` it had, which is where it was last heard, and the UI shows it as
 * unpinned so the operator can say where it belongs now.
 */
export function placeSpans(input: {
  plan: DocumentPlan;
  tracks: readonly ProjectAudioTrack[];
}): SpanPlacement[] {
  const order = input.plan.elements;
  const indexOf = new Map(
    order.map((element, index) => [element.elementId, index]),
  );

  const placements: SpanPlacement[] = [];
  for (const track of input.tracks) {
    const span = spanOf(track);
    if (!span) continue;
    const fromIndex = indexOf.get(span.from);
    const toIndex = indexOf.get(span.to);
    if (fromIndex === undefined || toIndex === undefined) continue;
    // Tolerated rather than refused: the two ends can end up the wrong way
    // round after a reorder, and the run between them is still what was meant.
    const first = Math.min(fromIndex, toIndex);
    const last = Math.max(fromIndex, toIndex);
    const run = order.slice(first, last + 1);
    const startMs = run[0]!.startMs;
    const endMs = run[run.length - 1]!.endMs;
    const keptMs = audioTrackKeptMs(track);
    placements.push({
      trackId: track.id,
      role: audioSegmentRole(track),
      elementIds: run.map((element) => element.elementId),
      startMs,
      endMs,
      spanMs: endMs - startMs,
      keptMs,
      overrunMs: Math.max(0, keptMs - (endMs - startMs)),
    });
  }
  return placements;
}

/**
 * The tracks with every pinned one moved onto its span.
 *
 * Returns the same array identity when nothing moved, so a caller can skip the
 * write — this runs after every document edit, and rewriting the lane on each
 * one would touch the project's `updatedAt` for a change that did not happen.
 */
export function applySpans(input: {
  plan: DocumentPlan;
  audio: ProjectAudioRecord;
}): ProjectAudioTrack[] {
  const byTrack = new Map(
    placeSpans({ plan: input.plan, tracks: input.audio.tracks }).map(
      (placement) => [placement.trackId, placement.startMs],
    ),
  );
  let moved = false;
  const next = input.audio.tracks.map((track) => {
    const startMs = byTrack.get(track.id);
    if (startMs === undefined || track.startMs === startMs) return track;
    moved = true;
    return { ...track, startMs };
  });
  return moved ? next : input.audio.tracks;
}

/** One scene, as a span picker offers it. */
export interface SpanCandidate {
  elementId: string;
  /** Its place in the film, 1-based, and what it is. */
  label: string;
  startMs: number;
  endMs: number;
  /** True when another track of this role already owns it. */
  taken: boolean;
}

const KIND_NOUN: Record<string, string> = {
  video: "Video",
  picture: "Picture",
  text: "Card",
  collage: "Collage",
};

/** What a scene is called in a span picker. */
function labelFor(element: PlannedElement, position: number): string {
  const noun = KIND_NOUN[element.kind] ?? "Scene";
  return element.sectionName
    ? `${position}. ${noun} · ${element.sectionName}`
    : `${position}. ${noun}`;
}

/** The whole film as one span, which is what a music bed usually wants. */
export function wholeFilmSpan(
  plan: DocumentPlan,
): { fromElementId: string; toElementId: string } | null {
  const first = plan.elements[0];
  const last = plan.elements[plan.elements.length - 1];
  if (!first || !last) return null;
  return { fromElementId: first.elementId, toElementId: last.elementId };
}

/**
 * The scenes a track may be pinned across.
 *
 * Every scene in the film, each marked with whether another track of the SAME
 * role already owns it. The caller offers the free ones and this track's own —
 * which is what makes "one voiceover per scene" a thing the UI cannot express
 * rather than a thing it has to detect afterwards.
 *
 * Roles are independent: a bed under a scene and a voice over it is the
 * ordinary case, so music never marks a scene taken for speech.
 */
export function availableElements(input: {
  plan: DocumentPlan;
  tracks: readonly ProjectAudioTrack[];
  /** The track being assigned — its own scenes are never "taken". */
  forTrackId: string;
  role: AudioSegmentRole;
}): SpanCandidate[] {
  const owned = new Set<string>();
  for (const placement of placeSpans({
    plan: input.plan,
    tracks: input.tracks,
  })) {
    if (placement.role !== input.role) continue;
    if (placement.trackId === input.forTrackId) continue;
    for (const id of placement.elementIds) owned.add(id);
  }
  return input.plan.elements.map((element, index) => ({
    elementId: element.elementId,
    label: labelFor(element, index + 1),
    startMs: element.startMs,
    endMs: element.endMs,
    taken: owned.has(element.elementId),
  }));
}

/**
 * The scenes that could be the END of a span starting at `fromElementId`.
 *
 * A span is a RUN, so the choice stops at the first scene someone else owns —
 * offering one past it would let a span swallow another track's scenes, and the
 * exclusivity would have to be repaired rather than never broken.
 */
export function spanEndCandidates(
  candidates: readonly SpanCandidate[],
  fromElementId: string,
): SpanCandidate[] {
  const start = candidates.findIndex(
    (candidate) => candidate.elementId === fromElementId,
  );
  if (start < 0) return [];
  const run: SpanCandidate[] = [];
  for (let i = start; i < candidates.length; i += 1) {
    const candidate = candidates[i]!;
    if (i > start && candidate.taken) break;
    run.push(candidate);
  }
  return run;
}
